import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {formatCurrency} from "../../../utils/currencyUtil";
import {useGetBomItemsByProjectByProjectIdQuery} from "../../../redux/vmsApi";

const ComparativeAnalysis = ({ quotations, user, projectId, dateReviewed }: { quotations: any[]; user: any; projectId?: string; dateReviewed?: string | Date | null }) => {
  const submittedAt = dateReviewed ? new Date(String(dateReviewed)) : null;
  const submittedAtLabel = submittedAt && !Number.isNaN(submittedAt.getTime())
    ? submittedAt.toLocaleString('en-GB')
    : 'N/A';
  const analysisData: any = {};
  const {data: bomItems = []} = useGetBomItemsByProjectByProjectIdQuery(
    {projectId: projectId ?? '', limit: 2000, skip: 0},
    {skip: !projectId},
  );

  interface Item {
    currency: any;
    supplier: string;
    itemCode: string;
    description: string;
    rate: number;
    discount: number;
    totalCost: number;
    quantity: number;
  }

  const normalize = (value?: string) => String(value ?? '').trim().toLowerCase();
  const makePairKey = (code?: string, description?: string) => `${normalize(code)}||${normalize(description)}`;

  const bomByCode = new Map<string, any>();
  const bomByDescription = new Map<string, any>();
  const bomByCodeAndDescription = new Map<string, any>();
  bomItems.forEach((item: any) => {
    const code = normalize(item.itemCode);
    const description = normalize(item.itemDescription);
    if (code && !bomByCode.has(code)) bomByCode.set(code, item);
    if (description && !bomByDescription.has(description)) bomByDescription.set(description, item);
    if (code && description && !bomByCodeAndDescription.has(makePairKey(code, description))) {
      bomByCodeAndDescription.set(makePairKey(code, description), item);
    }
  });

  const findBomItem = (item: Item) => {
    const code = normalize(item.itemCode);
    const description = normalize(item.description);

    // Primary rule: when both values exist, require a combined code+description match.
    if (code && description) {
      return bomByCodeAndDescription.get(makePairKey(code, description));
    }

    // Fallbacks for incomplete data.
    if (description) return bomByDescription.get(description);
    if (code) return bomByCode.get(code);
    return undefined;
  };

  const getComparableBomValue = (item: Item) => {
    const bomItem = findBomItem(item);
    console.log(' Comparing item:', item, 'with BOM item:', bomItem);
    if (!bomItem) return null;
    // Rate stored on BOM item is already VAT-inclusive when isVAT=true — no further VAT addition needed.
    const total = (Number(bomItem.itemBudgetedCost) || 0) * (Number(item.quantity) || 0);
    return {
      bomItem,
      total,
      vatApplied: !!bomItem.isVAT,
    };
  };
  
  quotations.forEach((quotation) => {
    quotation.quotationItems?.forEach((item: any) => {
      const { description, itemCode, rate, totalCostPerItem, discountPerItem, quantity } = item;
      const itemData = {
        currency: quotation.currency,
        supplier: quotation.supplier.name,
        itemCode: String(itemCode ?? ''),
        description: String(description ?? ''),
        rate: parseFloat(rate) || 0,
        discount: parseFloat(discountPerItem) || 0,
        totalCost: parseFloat(totalCostPerItem) || 0,
        quantity: quantity?.quantityValue || 0,
      };

      if (!analysisData[description]) {
        analysisData[description] = [];
      }
      
      analysisData[description].push(itemData);
    });
  });

 
  const supplierProvidesAllItems = (supplierName: string) => {
    return Object.values(analysisData as Record<string, Item[]>).every((items) =>
      items.some((item) => item.supplier === supplierName && item.totalCost > 0)
    );
  };
  
  const getBestAndWorstSuppliers = (suppliers: any[]) => {
    const totalCosts = suppliers.map((s: any) => s.totalCost);
    const minCost = Math.min(...totalCosts);
    const maxCost = Math.max(...totalCosts);

    return {
      best: suppliers.find((s: any) => s.totalCost === minCost)?.supplier,
      worst: suppliers.find((s: any) => s.totalCost === maxCost)?.supplier,
      minCost,
      maxCost,
    };
  };

 
  const supplierSummary = quotations.map((q) => ({
    currency:q.currency,
    deliveryLeadTime:q.deliveryLeadTime,
    supplier: q.supplier.name,
    totalBeforeVAT: q.totalb4VAT,
    totalVAT: q.totalVAT,
    grandTotal: q.grandTotal,
    totalDiscount: q.totalDiscount,
    providesAllItems: supplierProvidesAllItems(q.supplier.name),
  }));

  
  const recommendedSupplier = supplierSummary
  .filter((s) => s.providesAllItems) 
  .reduce(
    (prev, curr) =>
      prev === null || curr.grandTotal < prev.grandTotal ? curr : prev,
    null as { 
      currency:any;
      deliveryLeadTime:any;
      supplier: any; 
      totalBeforeVAT: any; 
      totalVAT: any; 
      grandTotal: any; 
      totalDiscount: any; 
      providesAllItems: boolean; 
    } | null
  );


  return (
    <Box sx={{ paddingY: 3, backgroundColor: "#f5f5f5" }}>
    
      <Card
        sx={{
          marginBottom: 3,
          borderRadius: 2,
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        }}
      >
        <CardContent>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#1976d2" }}
          >
            Procurement Officer Details
          </Typography>
          <Typography variant="body1">
            <strong>Name:</strong> {user.name}
          </Typography>
          {/* <Typography variant="body1">
            <strong>Job Title:</strong> {user.jobTitle}
          </Typography> */}
          <Typography variant="body1">
            <strong>Department:</strong> {user.department}
          </Typography>
          <Typography variant="body1">
            <strong>Email:</strong> {user.email}
          </Typography>
          <Typography variant="body1">
            <strong>Application Submitted:</strong> {submittedAtLabel}
          </Typography>
        </CardContent>
      </Card>

     
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontWeight: "bold", color: "#1976d2", marginBottom: 2 }}
      >
        Comparative Analysis
      </Typography>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
          overflow: "hidden",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1976d2" }}>
              <TableCell
                sx={{ color: "white", fontWeight: "bold" }}
              >{`Item Description (Quantity)`}</TableCell>
              <TableCell
                sx={{
                  color: "#0b3d2e",
                  fontWeight: "bold",
                  textAlign: "center",
                  backgroundColor: "#ffe082",
                }}
              >
                BOM Benchmark
              </TableCell>
              {quotations.map((q, index) => (
                <TableCell
                  key={index}
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  {q.supplier.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(analysisData).map(([description, suppliers]: any) => {
              const { minCost, maxCost } = getBestAndWorstSuppliers(suppliers);
              const bomComparable = getComparableBomValue(suppliers[0]);

              return (
                <TableRow key={description}>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {description}{" "}
                    {suppliers[0]?.quantity && (
                      <span>({suppliers[0].quantity})</span>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      backgroundColor: "#fff8e1",
                      color: "#3e2723",
                      fontWeight: "bold",
                      borderLeft: "3px solid #f9a825",
                    }}
                  >
                    {bomComparable ? (
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>
                          {formatCurrency(bomComparable.total, suppliers[0]?.currency || 'usd')}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          Rate: {formatCurrency(Number(bomComparable.bomItem.itemBudgetedCost) || 0, suppliers[0]?.currency || 'usd')}
                        </Typography>
                        <Chip
                          size="small"
                          label={bomComparable.vatApplied ? 'VAT Inclusive' : 'Excl. VAT'}
                          color={bomComparable.vatApplied ? 'warning' : 'default'}
                          variant={bomComparable.vatApplied ? 'filled' : 'outlined'}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        BOM item not linked
                      </Typography>
                    )}
                  </TableCell>
                  {suppliers.map((s: any, index: number) => {
                    const backgroundColor =
                      s.totalCost === 0
                        ? "#FFEB3B"
                        : s.totalCost === minCost
                        ? "lightgreen"
                        : s.totalCost === maxCost
                        ? "lightcoral"
                        : "rgba(255, 193, 7, 0.2)"; 

                    return (
                      <TableCell
                        key={index}
                        sx={{
                          textAlign: "center",
                          backgroundColor,
                          color: "black",
                          fontWeight: "bold",
                        }}
                      >
                        {s.totalCost === 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <WarningAmberIcon sx={{ marginRight: 1 }} />
                            Out of stock
                          </Box>
                        ) : (
                          `${formatCurrency(s.totalCost,s.currency||'usd')}`
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

     
      <Typography
        variant="h6"
        sx={{ fontWeight: "bold", color: "#1976d2", marginTop: 3 }}
      >
        Supplier Summary
      </Typography>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
          overflow: "hidden",
          marginTop: 2,
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1976d2" }}>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Supplier
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Total Before VAT
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Total VAT
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Grand Total
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Total Discount
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Provides All Items
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Expected Delivery Lead Time
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {supplierSummary.map((s, index) => (
              <TableRow key={index}>
                <TableCell>{s.supplier}</TableCell>
                <TableCell>{formatCurrency(s.totalBeforeVAT,s.currency||'usd')}</TableCell>
                <TableCell>{formatCurrency(s.totalVAT,s.currency||'usd')}</TableCell>
                <TableCell>{formatCurrency(s.grandTotal,s.currency||'usd')}</TableCell>
                <TableCell>{formatCurrency(s.totalDiscount,s.currency||'usd')}</TableCell>
                <TableCell>
                  {s.providesAllItems ? "Yes" : "No"}
                </TableCell>
                <TableCell>
                  {s.deliveryLeadTime } (Days)
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      
      <Box sx={{ marginTop: 3 }}>
        {recommendedSupplier ? (
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "green" }}
          >
            System Recommended Supplier: {recommendedSupplier.supplier} (All Items Provided, Least Grand Total)
          </Typography>
        ) : (
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "red" }}
          >
            No supplier provides all required items.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ComparativeAnalysis;
 
